import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Sun, Rocket, Compass as CompassIcon, Sparkles } from "lucide-react";
import useAuth from "../../hooks/useAuth";
import useRetryFetch from "../../hooks/useRetryFetch";
import { useIsMobile } from "../../hooks/use-mobile";
import { config } from "../../Environment";
import WelcomeSection from "./WelcomeSection";
import TodayCard from "./TodayCard";
import LifeProjects from "./cockpit/LifeProjects";
import Compass from "./cockpit/Compass";
import { SeederInvite } from "./cockpit/LifeOsSeeder";
import { RitualCard } from "./cockpit/Rituals";
import { PlantedCard } from "./cockpit/PlantedCard";
import { SeasonStrip } from "./cockpit/SeasonStrip";
import { useCockpitState } from "./cockpit/cockpitState";
import { plantedPhase, bumpPlantedVisits } from "../../lib/plantedLifecycle";
import ContinueStrip from "../portal/myActivities/ContinueStrip";
import DiscoverSection from "./DiscoverSection";
import GrowthFeed, { QuickInputCard } from "./GrowthFeed";
import GuestHome from "./GuestHome";
import OnboardingProgramSection from "./OnboardingProgramSection";
import StudioHome from "./StudioHome";
import { isStudioUser } from "../../lib/userType";

const HomePage = () => {
  const { t } = useTranslation();
  const { auth, user } = useAuth();
  const isLoggedIn = !!auth?.email && !!auth?.accessToken;
  const isMobile = useIsMobile();
  const retryFetch = useRetryFetch();
  const [runningProcesses, setRunningProcesses] = useState<any[]>([]);
  const [fetchKey, setFetchKey] = useState(0);
  const [mode, setModeState] = useState<CockpitMode>(() => {
    const stored = localStorage.getItem(MODE_KEY);
    return stored === "projects" || stored === "compass" ? stored : "today";
  });
  const setMode = (m: CockpitMode) => {
    setModeState(m);
    localStorage.setItem(MODE_KEY, m);
  };

  const cockpit = useCockpitState();
  const [visits, setVisits] = useState(0);
  const bumped = useRef(false);
  useEffect(() => {
    if (!bumped.current && !cockpit.isLoading && cockpit.seededAt) {
      bumped.current = true;
      setVisits(bumpPlantedVisits());
    }
  }, [cockpit.isLoading, cockpit.seededAt]);
  const planted = plantedPhase({
    seededAt: cockpit.seededAt,
    plantedCollapsedAt: cockpit.plantedCollapsedAt,
    visits,
  });

  // Coaches/experts (userType=expert) get the Studio home by default, with the
  // personal home one click away (persisted preference).
  const isStudio = isStudioUser(user);
  const [homeView, setHomeViewState] = useState<"studio" | "personal">(() =>
    localStorage.getItem(HOME_VIEW_KEY) === "personal" ? "personal" : "studio"
  );
  const setHomeView = (v: "studio" | "personal") => {
    setHomeViewState(v);
    localStorage.setItem(HOME_VIEW_KEY, v);
  };

  // Fetch running processes for ContinueStrip
  useEffect(() => {
    if (!isLoggedIn) return;
    const fetchRunning = async () => {
      try {
        const response = await retryFetch(
          `${config.API_BASE}/api/user/process/search?status=RUNNING&limit=20`
        );
        if (response.ok) {
          const data = await response.json();
          setRunningProcesses(data.list || data.processes || data || []);
        }
      } catch {
        // silently fail
      }
    };
    fetchRunning();
  }, [isLoggedIn, fetchKey]);

  // Group flat processes into prescription-shaped objects for ContinueStrip
  const prescriptionsForStrip = useMemo(() => {
    const grouped: Record<string, any> = {};
    for (const proc of runningProcesses) {
      const key = proc.fiPrescription?._id || proc.fiPrescription || "unknown";
      if (!grouped[key]) {
        grouped[key] = {
          _id: key,
          name: proc.prescriptionName || "Activity",
          valid: true,
          processes: [],
          settings: {},
          engagementCount: 0,
        };
      }
      grouped[key].processes.push(proc);
      grouped[key].engagementCount++;
    }
    return Object.values(grouped);
  }, [runningProcesses]);

  if (!isLoggedIn) {
    return <GuestHome />;
  }

  if (isStudio && homeView === "studio") {
    return <StudioHome onSwitchToPersonal={() => setHomeView("personal")} />;
  }

  const firstName =
    user?.firstName || user?.displayName?.split(" ")[0] || auth.email?.split("@")[0] || "";

  const hasRunning = prescriptionsForStrip.length > 0;

  // The switch travels into whichever mode owns the header row — Projects and
  // Compass render their own page titles (5a/5b). It is always the LAST item
  // in the header cluster, so it sits in the same spot in every mode.
  const headerControls = <CockpitModeSwitch mode={mode} onChange={setMode} />;

  return (
    <div className="flex-1 overflow-y-auto scroll-smooth">
      {/* Wide cockpit (designs 2a/5a/5b): the old single 896px column becomes a
          full-width board — the day in a main column, everything companion-
          and-capture-shaped batched into a right rail. Projects and Compass
          share the same wide frame instead of a narrow centered column. */}
      <div className="max-w-[1220px] mx-auto px-6 md:px-8 py-6">
        {/* Studio escape hatch lives in its own eyebrow row above every mode —
            never inside the header cluster, so the mode switch stays put.
            Mirrors the "My personal home" pill on the Studio side. */}
        {isStudio && (
          <div className="flex justify-end mb-3">
            <button
              onClick={() => setHomeView("studio")}
              className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white/70 px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              <Sparkles className="h-3.5 w-3.5 text-primary" />
              {t("studio_home_back", "Studio home")}
            </button>
          </div>
        )}
        {mode === "projects" ? (
          <LifeProjects headerExtra={headerControls} />
        ) : mode === "compass" ? (
          <Compass headerExtra={headerControls} />
        ) : (
          <>
        {/* Header row — the mode switch sits beside the greeting instead of
            costing a full row of vertical space. */}
        <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3 mb-6">
          <WelcomeSection firstName={firstName} seasonLine={cockpit.season?.line ?? null} />
          <div className="flex flex-wrap items-center gap-2 pb-1">{headerControls}</div>
        </div>
          {planted === "season" && (
            <div className="mb-5">
              <SeasonStrip onOpenCompass={() => setMode("compass")} />
            </div>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_332px] gap-5 items-start">
            {/* Main column — the day itself */}
            <div className="min-w-0 space-y-5">
              <SeederInvite />
              {planted === "planted" && (
                <PlantedCard onOpenProjects={() => setMode("projects")} />
              )}
              <TodayCard />
              <ContinueStrip
                prescriptions={prescriptionsForStrip}
                isMobile={isMobile}
                refreshLists={() => setFetchKey((k) => k + 1)}
              />
              {!cockpit.seededAt && !hasRunning && <DiscoverSection />}
              <GrowthFeed />
              {!cockpit.seededAt && hasRunning && <DiscoverSection />}
              {cockpit.seededAt && <DiscoverSection variant="pills" />}
            </div>
            {/* Right rail — companion, program, capture batched together.
                On mobile it stacks first, keeping the due-ritual card and
                capture above the fold as before. */}
            <div className="order-first lg:order-none space-y-4 lg:sticky lg:top-6 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto no-scrollbar">
              <RitualCard />
              <OnboardingProgramSection />
              <QuickInputCard />
            </div>
          </div>
          </>
        )}
      </div>
    </div>
  );
};

// ── Home mode switch — Today / Projects / Compass as three ways to look at
// the same life, not three separate apps ──────────────────────────────────────

type CockpitMode = "today" | "projects" | "compass";

const MODE_KEY = "cockpit_mode";
const HOME_VIEW_KEY = "home_view";

const CockpitModeSwitch = ({
  mode,
  onChange,
}: {
  mode: CockpitMode;
  onChange: (m: CockpitMode) => void;
}) => {
  const { t } = useTranslation();
  const modes: Array<{ key: CockpitMode; icon: React.ElementType; label: string; seat: string }> = [
    { key: "today", icon: Sun, label: t("cockpit_mode_today"), seat: t("cockpit_seat_doer") },
    { key: "projects", icon: Rocket, label: t("cockpit_mode_projects"), seat: t("cockpit_seat_manager") },
    { key: "compass", icon: CompassIcon, label: t("cockpit_mode_compass"), seat: t("cockpit_seat_visionary") },
  ];
  return (
    <div className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white/70 p-1">
      {modes.map(({ key, icon: Icon, label, seat }) => (
        <button
          key={key}
          onClick={() => onChange(key)}
          aria-pressed={mode === key}
          className={
            mode === key
              ? "flex flex-col items-center rounded-full bg-primary text-primary-foreground px-4 py-1.5 min-h-[44px] justify-center"
              : "flex flex-col items-center rounded-full px-4 py-1.5 min-h-[44px] justify-center text-muted-foreground hover:text-foreground"
          }
        >
          <span className="inline-flex items-center gap-1.5 text-sm font-medium leading-tight">
            <Icon className="w-3.5 h-3.5" />
            {label}
          </span>
          <span className={mode === key ? "text-[10px] leading-tight opacity-80" : "text-[10px] leading-tight text-muted-foreground/60"}>
            {seat}
          </span>
        </button>
      ))}
    </div>
  );
};

export default HomePage;
